/**
 * The own swing's presentation gate (`q-player-attack-no-restart`, owner ruling
 * 2026-09-25: "an attack animation is never cut back to its start by the next press").
 *
 * Every press is still SENT (the server's cooldown is authoritative; the client never
 * delays or drops a hit to protect a pose). This decides only what the own rig SHOWS:
 *
 *   - a press the server will refuse (inside the cooldown of the last press it would
 *     accept, less one tick of tolerance for quantisation) shows NOTHING — before this, a
 *     mashed key restarted the swing from frame 0 on every refused press;
 *   - a press the server will accept while the previous swing is still on screen is
 *     QUEUED and starts the moment that swing completes (a chain, cross-faded by the
 *     animator — never a restart);
 *   - otherwise the swing starts now.
 *
 * With the class clips played whole inside the cadence (actionClips.ts), a queued swing
 * waits at most one tick. A clip longer than the cadence (past the readable-rate cap)
 * chains late but never restarts; one queued swing at most, so the visual never builds a
 * backlog behind the hits.
 *
 * Pure + node-safe (AD-120): times are caller-supplied milliseconds; no clock is read.
 */

export type SwingVerdict = 'start' | 'queue' | 'ignore';

/**
 * A swing that starts within this long after the previous one completed CONTINUES the
 * combo (AD-670); a longer pause returns to step 1. `[tune]` — pressing at the 0.6 s
 * cadence starts each swing exactly as the last completes, so any steady mash chains.
 */
export const COMBO_CHAIN_GRACE_MS = 300;

export class SwingGate {
  private lastAcceptedMs = Number.NEGATIVE_INFINITY;
  private activeUntilMs = Number.NEGATIVE_INFINITY;
  private pending = false;
  private step = 0;

  /**
   * @param cooldownSeconds the server's swing cadence (PLAYER_ATTACK_COOLDOWN_SECONDS)
   * @param toleranceSeconds how early a press may come and still be accepted — one tick
   */
  constructor(
    private readonly cooldownSeconds: number,
    private readonly toleranceSeconds: number,
  ) {}

  /**
   * A press at `nowMs`. `showing` = the rig is still presenting the last swing (the
   * animator's active action is `attack`) — a swing cut by movement is not showing.
   */
  press(nowMs: number, showing: boolean): SwingVerdict {
    const earliest = this.lastAcceptedMs + (this.cooldownSeconds - this.toleranceSeconds) * 1000;
    if (nowMs < earliest) return 'ignore'; // the server will refuse it: nothing to show
    this.lastAcceptedMs = nowMs;
    if (showing && nowMs < this.activeUntilMs) {
      this.pending = true;
      return 'queue';
    }
    return 'start';
  }

  /**
   * The combo step a swing starting at `nowMs` should play (AD-670): the next step when it
   * follows the previous swing's completion within `COMBO_CHAIN_GRACE_MS`, else 0. The
   * caller wraps it by the class's chain length.
   */
  stepFor(nowMs: number): number {
    return nowMs <= this.activeUntilMs + COMBO_CHAIN_GRACE_MS ? this.step + 1 : 0;
  }

  /** A swing was actually presented at `nowMs`, at combo `step`, owning the pose for `wallSeconds`. */
  begin(nowMs: number, wallSeconds: number, step = 0): void {
    this.pending = false;
    this.activeUntilMs = nowMs + Math.max(0, wallSeconds) * 1000;
    this.step = step;
  }

  /** Per frame: whether the queued swing is due now (the previous one completed or was cut). */
  poll(nowMs: number, showing: boolean): boolean {
    if (!this.pending) return false;
    if (showing && nowMs < this.activeUntilMs) return false;
    this.pending = false;
    return true;
  }

  /** Forget everything (death, respawn, zone change). */
  reset(): void {
    this.lastAcceptedMs = Number.NEGATIVE_INFINITY;
    this.activeUntilMs = Number.NEGATIVE_INFINITY;
    this.pending = false;
    this.step = 0;
  }

  /** Whether a swing is queued (tests + scene dump). */
  get queued(): boolean {
    return this.pending;
  }
}
